import { Info, ShieldCheck, TriangleAlert } from '@orgus/ui/icons';
import type { JoinToken } from './revoke.ts';

/**
 * The one note when the hub says no other machine can reach it (it listens on loopback only):
 * that is the default, not a fault, so it is calm and says the safe ways on. The hub's own
 * warning text is not shown: it may advise listening on every address, which the exposure hold
 * forbids until the transport is hardened.
 */
export function ReachabilityNotice({ token }: { token: Pick<JoinToken, 'warning' | 'hubUrl'> }) {
  if (!token.warning) return null;
  return (
    <div role="status" className="flex gap-3 rounded-card border border-border bg-bg px-3 py-3 text-ui" data-testid="join-unreachable">
      <Info className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-fg">Machines on other computers are off</p>
        <p className="text-fg-muted">
          This command works on this computer only. To add another, use Add machine on it, or turn on Settings, Trust, Machines on other computers, only behind HTTPS or a private
          network such as Tailscale.
        </p>
      </div>
    </div>
  );
}


/** Said up front: the command runs inside a copy of Orgus on the new machine. */
export function NeedsCheckout() {
  return (
    <div className="flex gap-3 rounded-card border border-border bg-bg px-3 py-3 text-ui">
      <Info className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden />
      <p className="min-w-0 flex-1 text-fg-muted">
        <span className="font-medium text-fg">The new machine needs the Orgus node agent.</span> Download it above, or build it from agents/node-agent in an Orgus checkout, and run the command in the folder that holds{' '}
        <code className="rounded-control bg-surface px-1 font-mono text-caption text-fg">orgus-node-agent</code>.
      </p>
    </div>
  );
}

/**
 * COMPAT-3: which hub key the command pins, or why it pins none. Without the pin, an agent on
 * another computer over plain HTTP refuses to connect, so the person learns why here, not there.
 */
export function HubKeyNotice({ token }: { token: Pick<JoinToken, 'hubKeyFingerprint' | 'hubKeyNote'> }) {
  if (token.hubKeyFingerprint) {
    return (
      <div className="flex gap-3 rounded-card border border-border bg-bg px-3 py-3 text-ui" data-testid="join-hub-key">
        <ShieldCheck className="mt-1 size-4 shrink-0 text-status-success" aria-hidden />
        <p className="min-w-0 flex-1 text-fg-muted">
          <span className="font-medium text-fg">The command trusts only this hub.</span> It pins the hub key{' '}
          <code className="break-all rounded-control bg-surface px-1 font-mono text-caption text-fg">{token.hubKeyFingerprint}</code>, the fingerprint Settings, Trust shows.
        </p>
      </div>
    );
  }
  if (!token.hubKeyNote) return null;
  return (
    <div role="alert" className="flex gap-3 rounded-card border border-status-attention-line bg-status-attention-soft px-3 py-3 text-ui" data-testid="join-hub-key-withheld">
      <TriangleAlert className="mt-1 size-4 shrink-0 text-status-attention" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-status-attention-ink">This command cannot pin the hub key</p>
        <p className="text-fg-muted">{token.hubKeyNote}</p>
      </div>
    </div>
  );
}
